import type { OverlayNode, OverlayZIndexLane } from '@inf-monkeys-tech/monkeys';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

import { BaseDialog } from '../base/BaseDialog';
import type { BaseAppearance, BaseDialogClassNames } from '../base/types';
import {
  getRenderNodeDataAttributes,
  RenderNodeHost,
} from './RenderNodeHost';
import type {
  RenderNodeHostProps,
  RenderNodeRuntimeState,
} from './RenderNodeHost';

export interface OverlayHistoryAdapter {
  getCurrentUrl(): string;
  push(url: string): void;
  replace(url: string): void;
  back(): void;
  subscribe(listener: (url: string) => void): () => void;
}

export interface OverlayNodeRuntimeContext extends RenderNodeRuntimeState {
  open: boolean;
  zIndexLane: OverlayZIndexLane;
  zIndex: number;
  initialFocusRef?: RefObject<HTMLElement | null>;
  focusReturnRef?: RefObject<HTMLElement | null>;
  renderNodeAttributes: ReturnType<typeof getRenderNodeDataAttributes>;
  onOpenChange(open: boolean): void;
  requestClose(): void;
}

export interface OverlayNodeHostProps {
  node: OverlayNode;
  open: boolean;
  renderStrategy?: 'dialog' | 'delegate';
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  children?: ReactNode | ((runtime: OverlayNodeRuntimeContext) => ReactNode);
  appearance?: BaseAppearance;
  className?: string;
  classNames?: BaseDialogClassNames;
  initialFocusRef?: RefObject<HTMLElement | null>;
  focusReturnRef?: RefObject<HTMLElement | null>;
  historyAdapter?: OverlayHistoryAdapter | false;
  manualQueryEnabled?: boolean;
  onActivate?: RenderNodeHostProps['onActivate'];
  onRuntimeStateChange?: RenderNodeHostProps['onRuntimeStateChange'];
  onOpenChange: (open: boolean) => void;
}

const OVERLAY_Z_INDEX: Record<OverlayZIndexLane, number> = {
  popover: 40,
  drawer: 50,
  modal: 60,
  fullscreen: 70,
  system: 80,
};

export const getOverlayZIndex = (lane: OverlayZIndexLane) => OVERLAY_Z_INDEX[lane];

const OVERLAY_PRESENTATION_CLASS_NAMES: Record<
  OverlayNode['presentation'],
  Pick<BaseDialogClassNames, 'body' | 'content' | 'panel'>
> = {
  drawer: {
    content: '!items-stretch !justify-end !p-0',
    panel: '!h-dvh !max-h-none !w-full !max-w-[min(100vw,40rem)] !rounded-none !border-y-0 !border-r-0',
    body: 'min-h-0 flex-1 overflow-auto',
  },
  modal: {},
  fullscreen: {
    content: '!items-stretch !justify-stretch !p-0',
    panel: '!h-dvh !max-h-none !w-screen !max-w-none !rounded-none !border-0',
    body: 'min-h-0 flex-1 overflow-auto',
  },
};

export const getOverlayPresentationClassNames = (
  presentation: OverlayNode['presentation'],
): Pick<BaseDialogClassNames, 'body' | 'content' | 'panel'> => ({
  ...OVERLAY_PRESENTATION_CLASS_NAMES[presentation],
});

const relativeUrl = (input: string, url: URL) =>
  input.startsWith('/') && !input.startsWith('//')
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();

export const isOverlayUrlActive = (node: OverlayNode, input: string): boolean => {
  const url = new URL(input, 'https://monkeys.local');
  return url.searchParams.get(node.url.parameter) === node.url.value;
};

export const buildOverlayUrl = (node: OverlayNode, input: string, open: boolean): string => {
  const url = new URL(input, 'https://monkeys.local');
  if (open) url.searchParams.set(node.url.parameter, node.url.value);
  else if (url.searchParams.get(node.url.parameter) === node.url.value) url.searchParams.delete(node.url.parameter);
  return relativeUrl(input, url);
};

export const browserOverlayHistoryAdapter: OverlayHistoryAdapter = {
  getCurrentUrl() {
    return typeof window === 'undefined' ? 'https://monkeys.local/' : window.location.href;
  },
  push(url) {
    if (typeof window !== 'undefined') window.history.pushState(window.history.state, '', url);
  },
  replace(url) {
    if (typeof window !== 'undefined') window.history.replaceState(window.history.state, '', url);
  },
  back() {
    if (typeof window !== 'undefined') window.history.back();
  },
  subscribe(listener) {
    if (typeof window === 'undefined') return () => undefined;
    const handlePopState = () => listener(window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  },
};

const scheduleFocus = (target: HTMLElement | null | undefined) => {
  if (!target?.isConnected) return;
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => target.focus());
  } else {
    target.focus();
  }
};

export function OverlayNodeHost({
  node,
  open,
  renderStrategy = 'dialog',
  title,
  description,
  footer,
  closeLabel,
  children,
  appearance,
  className,
  classNames,
  initialFocusRef,
  focusReturnRef,
  historyAdapter = browserOverlayHistoryAdapter,
  manualQueryEnabled = false,
  onActivate,
  onRuntimeStateChange,
  onOpenChange,
}: OverlayNodeHostProps) {
  const historyEntryOwnedRef = useRef(false);
  const initializedUrlRef = useRef(false);
  const zIndex = getOverlayZIndex(node.zIndexLane);
  const syncUrl = useCallback((nextOpen: boolean) => {
    if (!node.renderNode.lifecycle.deepLink || historyAdapter === false) return;
    const currentUrl = historyAdapter.getCurrentUrl();
    if (isOverlayUrlActive(node, currentUrl) === nextOpen) return;
    const nextUrl = buildOverlayUrl(node, currentUrl, nextOpen);
    if (nextOpen) {
      if (node.url.openMode === 'push') {
        historyAdapter.push(nextUrl);
        historyEntryOwnedRef.current = true;
      } else {
        historyAdapter.replace(nextUrl);
        historyEntryOwnedRef.current = false;
      }
      return;
    }
    if (node.url.closeMode === 'back' && historyEntryOwnedRef.current) {
      historyEntryOwnedRef.current = false;
      historyAdapter.back();
    } else {
      historyAdapter.replace(nextUrl);
      historyEntryOwnedRef.current = false;
    }
  }, [historyAdapter, node]);

  const requestOpenChange = useCallback((nextOpen: boolean) => {
    syncUrl(nextOpen);
    onOpenChange(nextOpen);
  }, [onOpenChange, syncUrl]);

  useEffect(() => {
    if (!node.renderNode.lifecycle.deepLink || historyAdapter === false) return undefined;
    const urlOpen = isOverlayUrlActive(node, historyAdapter.getCurrentUrl());
    if (!initializedUrlRef.current) {
      initializedUrlRef.current = true;
      if (urlOpen && !open) onOpenChange(true);
      else if (!urlOpen && open) syncUrl(true);
    } else if (urlOpen !== open) {
      syncUrl(open);
    }
    return historyAdapter.subscribe((url) => {
      historyEntryOwnedRef.current = false;
      const nextOpen = isOverlayUrlActive(node, url);
      if (nextOpen !== open) onOpenChange(nextOpen);
    });
  }, [historyAdapter, node, onOpenChange, open, syncUrl]);

  useEffect(() => {
    if (!open || !node.renderNode.lifecycle.focusReturn || typeof document === 'undefined') return undefined;
    const target = focusReturnRef?.current
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    return () => scheduleFocus(focusReturnRef?.current ?? target);
  }, [focusReturnRef, node.renderNode.lifecycle.focusReturn, open]);

  const renderNodeAttributes = useMemo(
    () => getRenderNodeDataAttributes(node.renderNode),
    [node.renderNode],
  );
  const presentationClassNames = getOverlayPresentationClassNames(node.presentation);
  const resolvedClassNames: BaseDialogClassNames = {
    ...classNames,
    body: classNames?.body ?? presentationClassNames.body,
    content: classNames?.content ?? presentationClassNames.content,
    panel: classNames?.panel ?? presentationClassNames.panel,
  };

  return (
    <RenderNodeHost
      node={node.renderNode}
      active={open}
      visible={open}
      manualQueryEnabled={manualQueryEnabled}
      className={className}
      style={renderStrategy === 'delegate' ? { display: 'contents' } : undefined}
      onActivate={onActivate}
      onRuntimeStateChange={onRuntimeStateChange}
    >
      {(runtime) => {
        const context: OverlayNodeRuntimeContext = {
          ...runtime,
          open,
          zIndexLane: node.zIndexLane,
          zIndex,
          initialFocusRef,
          focusReturnRef,
          renderNodeAttributes,
          onOpenChange: requestOpenChange,
          requestClose: () => requestOpenChange(false),
        };
        const content = typeof children === 'function' ? children(context) : children;
        if (renderStrategy === 'delegate') return content;
        return (
          <BaseDialog
            id={node.overlayId}
            open={open}
            title={title}
            description={description}
            footer={footer}
            closeLabel={closeLabel}
            closeOnEscape={node.close.escape}
            closeOnOverlayClick={node.close.backdrop}
            keepMounted={node.renderNode.lifecycle.retainOnDeactivate}
            trapFocus={node.focus.trap}
            restoreFocus={false}
            initialFocus={node.focus.initial}
            initialFocusRef={initialFocusRef}
            zIndex={zIndex}
            appearance={appearance}
            classNames={resolvedClassNames}
            data-monkeys-overlay-presentation={node.presentation}
            data-monkeys-overlay-z-index-lane={node.zIndexLane}
            onOpenChange={requestOpenChange}
          >
            {content}
          </BaseDialog>
        );
      }}
    </RenderNodeHost>
  );
}
